import Api from 'client/api/v2/Api';
import axios from 'axios';

class SystemInsightsApi extends Api {
  constructor() {
    super();
    this.baseUrl = '/api/v2/systeminsights';
  }

  /**
   * This method gets all of the users from Systems Insights.
   * @see https://docs.jumpcloud.com/2.0/system-insights/list-system-insights-users for API docs
   * @param {Object} options.params - Parameters to be used in the non-bulk GET request
   * Pass in { details: 'v1' } to get all user details
   */

  getSystemInsightsUsers(params) {
    return axios.get(`${this.baseUrl}/users`, { params })
      .then(resp => ({
        results: resp.data,
        totalCount: +resp.headers['x-total-count'],
      }));
  }

  /**
   * This method abstracts the shared logic around System Insights API calls.
   * For a given URL, it performs a GET request and formats the response.
   * The method uses the inherited `getAllItems` method if it's a bulk request
   * @see https://docs.jumpcloud.com/2.0/system-insights for API docs
   * @see https://osquery.io/schema/3.3.2 for response details
   * @param {Boolean} options.bulk - Whether it is a bulk request (first 100 or all)
   * @param {String} endpoint - The API endpoint to be called
   * @param {String} id - The ID of the system
   * @param {Object} options.params - Parameters to be used in the non-bulk GET request
   * @param {Object} options - All options to be used with the request
   */
  getSystemInsight(id, endpoint, options = {}) {
    if (!id) throw new Error('Missing system id');
    if (!endpoint) throw new Error('Missing endpoint');

    const {
      bulk = false,
      params = {},
    } = options;

    // Add the `system_id` to the filter parameter
    if (!params.filter) params.filter = [];
    params.filter.push(`system_id:eq:${id}`);

    const url = `${this.baseUrl}/${endpoint}`;
    if (bulk) return this.getAllItems(url, params);
    return axios.get(url, { params });
  }

  /**
   * This method is similar to `getSystemInsight` but performs the API request for all systems
   * The method uses the inherited `getAllItems` method if it's a bulk request
   * @see https://docs.jumpcloud.com/2.0/system-insights for API docs
   * @see https://osquery.io/schema/3.3.2 for response details
   * @param {Boolean} options.bulk - Whether it is a bulk request (first 100 or all)
   * @param {String} endpoint - The API endpoint to be called
   * @param {Object} options.params - Parameters to be used in the non-bulk GET request
   * @param {Object} options - All options to be used with the request
   */
  getAllSystemInsights(endpoint, options = {}) {
    if (!endpoint) throw new Error('Missing endpoint');

    const {
      bulk = false,
      params = {},
    } = options;

    const url = `${this.baseUrl}/${endpoint}`;
    if (bulk) return this.getAllItems(url, params);
    return axios.get(url, { params });
  }
}

export default new SystemInsightsApi();
